import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../auth/AuthProvider";
import {
  getPortfolioSummary,
  getPortfolioProjects,
  getPortfolioAggregate,
  getPortfolioOverview,
  type PortfolioSummaryResponse,
  type PortfolioAggregate,
  type PortfolioOverviewResponse,
  type ProjectWithImpacts,
} from "../api/portfolio";

// Portfolio rollups run an expensive DISTINCT ON over the full project set's
// summary_json. Cache them long enough that navigating dashboard <-> projects
// <-> back doesn't re-trigger the ~25s query, while still refreshing within a
// working session.
const PORTFOLIO_STALE_MS = 1000 * 60 * 15;
const PORTFOLIO_GC_MS = 1000 * 60 * 30;

/**
 * Consolidated dashboard fetch — totals, groups, and BOTH analysis-mode
 * aggregates (ctg + ops) in a single backend round-trip. The dashboard
 * selects ctg/ops client-side, so toggling analysis mode no longer re-queries.
 */
export function usePortfolioOverview(
  groupBy?: string,
  filters?: Record<string, string>,
) {
  const { token } = useAuth();
  return useQuery<PortfolioOverviewResponse>({
    queryKey: ["portfolio-overview", groupBy, filters],
    queryFn: () => getPortfolioOverview(token!, groupBy, filters),
    enabled: !!token,
    staleTime: PORTFOLIO_STALE_MS,
    gcTime: PORTFOLIO_GC_MS,
    // Keep prior cards on screen while a group-by/filter change refetches.
    placeholderData: keepPreviousData,
  });
}

export function usePortfolioSummary(
  groupBy?: string,
  filters?: Record<string, string>,
) {
  const { token } = useAuth();
  return useQuery<PortfolioSummaryResponse>({
    queryKey: ["portfolio-summary", groupBy, filters],
    queryFn: () => getPortfolioSummary(token!, groupBy, filters),
    enabled: !!token,
    staleTime: 1000 * 60 * 5,
  });
}

export function usePortfolioAggregate(
  filters?: Record<string, string>,
  analysis?: "ctg" | "ops",
) {
  const { token } = useAuth();
  return useQuery<PortfolioAggregate>({
    queryKey: ["portfolio-aggregate", filters, analysis],
    queryFn: () => getPortfolioAggregate(token!, filters, analysis),
    enabled: !!token,
    staleTime: 1000 * 60 * 5,
    // Keep the previous analysis-mode data on screen while the new one fetches
    // so toggling Operations ↔ Cradle-to-Grave doesn't blank the cards.
    placeholderData: keepPreviousData,
  });
}

export function usePortfolioProjects(opts?: {
  sort?: string;
  limit?: number;
  offset?: number;
  filters?: Record<string, string>;
}) {
  const { token } = useAuth();
  return useQuery<ProjectWithImpacts[]>({
    queryKey: ["portfolio-projects", opts],
    queryFn: () => getPortfolioProjects(token!, opts),
    enabled: !!token,
    staleTime: PORTFOLIO_STALE_MS,
    gcTime: PORTFOLIO_GC_MS,
  });
}
